import React, { useState, useEffect } from "react";
import { Form, Row, Col, Tab, Tabs, Button, Spinner } from "react-bootstrap";
import { EditTemplate } from "./EditTemplate";
import NoteRenderer from "./NoteRenderer";
import { fetchTemplateVersions, fetchTemplateVersion } from "../../api";

export default function ViewTemplate({ selectedTemplate, onTemplateEdit, refresh, editedTemplate }) {
  const [template, setTemplate] = useState(null);

  useEffect(() => {
    if (!selectedTemplate) return;
    setTemplate(selectedTemplate);
  }, [selectedTemplate]);

  const handleTemplateUpdate = (updatedTemplate) => {
    setTemplate(updatedTemplate);
  };

  if (!template) return <p>Loading template…</p>;

  const isNewStyle = Array.isArray(template.pages) && template.pages.length > 0;

  // ── Shared header ──────────────────────────────────────────────────────────
  const header = (
    <div className="d-flex justify-content-between align-items-center">
      <h5 className="fw-normal text-primary">{template.templateTitle}</h5>
      <EditTemplate
        template={template}
        templateEdit={onTemplateEdit}
        editedTemplate={handleTemplateUpdate}
      />
    </div>
  );

  const statusBadge = (
    <>
      <p className="text-primary d-inline">Status: </p>
      <p
        className={`d-inline fw-bold ${
          template.templateStatus === "Active" ? "text-success" : "text-info"
        }`}
      >
        {template.templateStatus}
      </p>
    </>
  );

  // ── VersionHistoryPanel ───────────────────────────────────────────────────

  function VersionHistoryPanel({ templateId, currentVersion }) {
    const [versions, setVersions] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [selectedVersion, setSelectedVersion] = React.useState(null);
    const [loadingVersion, setLoadingVersion] = React.useState(false);
    const [error, setError] = React.useState(null);

    React.useEffect(() => {
      setLoading(true);
      fetchTemplateVersions(templateId)
        .then(setVersions)
        .catch((e) => setError(e.message))
        .finally(() => setLoading(false));
    }, [templateId]);

    const handlePreview = async (vNum) => {
      setLoadingVersion(true);
      setSelectedVersion(null);
      try {
        const v = await fetchTemplateVersion(templateId, vNum);
        setSelectedVersion(v);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoadingVersion(false);
      }
    };

    if (loading) return <div className="mt-3 text-muted small"><Spinner size="sm" /> Loading version history…</div>;
    if (error) return <div className="mt-3 text-danger small">Error: {error}</div>;
    if (!versions.length) return <div className="mt-3 text-muted small">No published versions yet.</div>;

    return (
      <div className="mt-3">
        <table className="table table-sm table-hover">
          <thead>
            <tr>
              <th>Version</th>
              <th>Published</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {versions.map((v) => (
              <tr key={v.versionNumber}>
                <td>
                  v{v.versionNumber}
                  {v.versionNumber === currentVersion
                    ? <span className="ms-2 badge bg-success">current</span>
                    : null}
                </td>
                <td className="text-muted small">
                  {new Date(v.savedAt).toLocaleString()}
                </td>
                <td>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => handlePreview(v.versionNumber)}
                    disabled={loadingVersion}
                  >
                    Preview
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {loadingVersion && <div className="text-muted small"><Spinner size="sm" /> Loading snapshot…</div>}

        {selectedVersion && (
          <div className="border rounded p-3 mt-2">
            <p className="text-muted small mb-2">
              v{selectedVersion.versionNumber} — Published {new Date(selectedVersion.savedAt).toLocaleString()}
            </p>
            <NoteRenderer pages={selectedVersion.pages} mode="readonly" />
          </div>
        )}
      </div>
    );
  }

  // ── New-style renderer (pages + colSpan grid) ─────────────────────────────

  if (isNewStyle) {
    return (
      <div>
        {header}
        {statusBadge}
        <Tabs defaultActiveKey="current" className="mt-3">
          <Tab eventKey="current" title="Current Version">
            <div className="mt-3 mb-3">
              <NoteRenderer pages={template.pages} mode="readonly" />
            </div>
          </Tab>
          <Tab eventKey="history" title="Version History">
            <VersionHistoryPanel
              templateId={String(template._id)}
              currentVersion={template.currentVersion || 0}
            />
          </Tab>
        </Tabs>
      </div>
    );
  }

  // ── Legacy renderer (notes[]) ─────────────────────────────────────────────

  return (
    <div>
      {header}
      {statusBadge}

      <Row className="mt-3 mb-3">
        {template.notes?.length > 0 ? (
          template.notes.map((note, index) => (
            <Col md={6} key={index}>
              <div className="container border shadow-sm rounded pt-2 position-relative p-3">
                <Form.Group controlId={`section-${index}`}>
                  <Form.Label className="text-primary fw-bold">
                    {note.sessionsSection}
                  </Form.Label>

                  {note.sessionsFieldType === "text" && (
                    <p className={note.sessionsSectionNotes ? "" : "text-muted"}>
                      {note.sessionsSectionNotes || "No default value"}
                    </p>
                  )}

                  {note.sessionsFieldType === "textarea" && (
                    <p className={note.sessionsSectionNotes ? "" : "text-muted"}>
                      {note.sessionsSectionNotes || "No default value"}
                    </p>
                  )}

                  {note.sessionsFieldType === "multiselect" &&
                    Array.isArray(note.sessionsSectionNotes) && (
                      <ul className="m-0 p-0">
                        {note.sessionsSectionNotes.length > 0
                          ? note.sessionsSectionNotes.map((option, i) => (
                              <li key={i} className="list-unstyled">{option}</li>
                            ))
                          : <li className="list-unstyled text-muted">No options selected.</li>
                        }
                      </ul>
                    )}

                  {note.sessionsFieldType === "image" && note.sessionsImageURL && (
                    <div className="mt-2">
                      <img
                        src={note.sessionsImageURL}
                        alt="Uploaded"
                        className="img-fluid rounded"
                        style={{ maxHeight: "200px", maxWidth: "100%" }}
                      />
                    </div>
                  )}
                </Form.Group>
              </div>
            </Col>
          ))
        ) : (
          <p>No notes available for the selected template.</p>
        )}
      </Row>
    </div>
  );
}
